export interface AnalysisResult {
  score: number;
  verdict: string;
  radarData: { axis: string; value: number }[];
  flagCount: number;
  manipulation_score: number;
  toxicity_score: number;
  confidence: "high" | "moderate" | "low";
  nuance: string;
  primary_pattern: string;
  secondary_pattern: string;
}

const FLAG_COUNT_BY_SCORE = (score: number) => {
  if (score >= 9) return 5;
  if (score >= 7) return 4;
  if (score >= 5) return 3;
  if (score >= 3) return 2;
  if (score >= 1) return 1;
  return 0;
};

const verdictFromScore = (score: number): string => {
  if (score >= 7) return "MANIPULATION DETECTED";
  if (score >= 4) return "AMBIGUOUS SIGNAL";
  return "LOW RISK / NEUTRAL";
};

const confidenceFromScore = (score: number): "high" | "moderate" | "low" => {
  if (score >= 7) return "high";
  if (score >= 4) return "moderate";
  return "high"; // low-risk assessments are confident
};

interface AnchorDef {
  keywords: string[];
  manipulation_score: number;
  toxicity_score: number;
  primary_pattern: string;
  secondary_pattern: string;
  confidence: "high" | "moderate" | "low";
  nuance: string;
  radarData: { axis: string; value: number }[];
}

const ANCHORS: AnchorDef[] = [
  // ══ LEVEL 1 — Healthy / Low Risk ══
  {
    keywords: ["i need space", "i need some space"],
    manipulation_score: 1.5,
    toxicity_score: 1.0,
    primary_pattern: "healthy_boundary",
    secondary_pattern: "healthy_boundary",
    confidence: "high",
    nuance: "This is healthy boundary-setting. The speaker requests space without blame, guilt, or punishment.",
    radarData: [
      { axis: "Gaslighting", value: 2 },
      { axis: "Invalidation", value: 3 },
      { axis: "Deflection", value: 5 },
      { axis: "Control", value: 2 },
      { axis: "Blame Shift", value: 2 },
    ],
  },
  {
    keywords: ["let's talk later", "let's discuss later"],
    manipulation_score: 1.2,
    toxicity_score: 0.8,
    primary_pattern: "neutral_accountability",
    secondary_pattern: "healthy_boundary",
    confidence: "high",
    nuance: "This is a neutral conversational pause. Deferring a discussion is healthy communication, not avoidance or stonewalling.",
    radarData: [
      { axis: "Gaslighting", value: 2 },
      { axis: "Invalidation", value: 3 },
      { axis: "Deflection", value: 8 },
      { axis: "Control", value: 2 },
      { axis: "Blame Shift", value: 2 },
    ],
  },
  {
    keywords: ["i don't want to continue this right now", "don't want to continue this"],
    manipulation_score: 1.5,
    toxicity_score: 1.0,
    primary_pattern: "neutral_accountability",
    secondary_pattern: "healthy_boundary",
    confidence: "high",
    nuance: "The speaker pauses the conversation without attaching blame or punishment. This is disengagement, not manipulation.",
    radarData: [
      { axis: "Gaslighting", value: 2 },
      { axis: "Invalidation", value: 3 },
      { axis: "Deflection", value: 6 },
      { axis: "Control", value: 2 },
      { axis: "Blame Shift", value: 2 },
    ],
  },
  {
    keywords: ["maybe i misunderstood", "i could be wrong", "my mistake", "maybe we're both misunderstanding"],
    manipulation_score: 2.1,
    toxicity_score: 1.5,
    primary_pattern: "neutral_accountability",
    secondary_pattern: "clarification",
    confidence: "high",
    nuance: "This acknowledges the possibility of being wrong — a sign of healthy communication rather than manipulation. No coercive intent detected.",
    radarData: [
      { axis: "Gaslighting", value: 5 },
      { axis: "Invalidation", value: 8 },
      { axis: "Deflection", value: 10 },
      { axis: "Control", value: 5 },
      { axis: "Blame Shift", value: 3 },
    ],
  },
  // ══ LEVEL 2 — Ambiguous / Insensitive ══
  {
    keywords: ["i don't remember it that way", "i don't recall", "that's not how i remember"],
    manipulation_score: 5.4,
    toxicity_score: 3.2,
    primary_pattern: "ambiguous_responsibility",
    secondary_pattern: "subjective_memory",
    confidence: "low",
    nuance: "This could be a genuine memory difference rather than deliberate distortion. Without aggressive denial or accusations, it falls into ambiguous territory.",
    radarData: [
      { axis: "Gaslighting", value: 55 },
      { axis: "Invalidation", value: 30 },
      { axis: "Deflection", value: 40 },
      { axis: "Control", value: 25 },
      { axis: "Blame Shift", value: 20 },
    ],
  },
  {
    keywords: ["that's not what happened", "that didn't happen"],
    manipulation_score: 5.6,
    toxicity_score: 3.5,
    primary_pattern: "ambiguous_responsibility",
    secondary_pattern: "reality_dispute",
    confidence: "low",
    nuance: "This disputes facts but doesn't accuse fabrication or question the other person's sanity. It's weaker than 'That never happened' because it challenges the event, not the person's reliability.",
    radarData: [
      { axis: "Gaslighting", value: 35 },
      { axis: "Invalidation", value: 30 },
      { axis: "Deflection", value: 40 },
      { axis: "Control", value: 25 },
      { axis: "Blame Shift", value: 20 },
    ],
  },
  // ══ COERCIVE SHUTDOWN / WITHDRAWAL ══
  {
    keywords: ["fine, i just won't talk anymore", "i just won't talk anymore", "i'll just keep quiet then", "i'll just keep quiet"],
    manipulation_score: 5.5,
    toxicity_score: 4.5,
    primary_pattern: "passive_aggression",
    secondary_pattern: "coercive_withdrawal",
    confidence: "moderate",
    nuance: "This frames silence as punishment rather than as a neutral pause. The implied message is: 'I'll withdraw and make you feel responsible for it.'",
    radarData: [
      { axis: "Gaslighting", value: 15 },
      { axis: "Invalidation", value: 40 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 60 },
      { axis: "Blame Shift", value: 50 },
    ],
  },
  {
    keywords: ["forget it", "you win"],
    manipulation_score: 4.8,
    toxicity_score: 4.0,
    primary_pattern: "passive_aggression",
    secondary_pattern: "coercive_withdrawal",
    confidence: "moderate",
    nuance: "This sounds like giving up, but often functions as emotional pressure — daring the other person to feel guilty for 'winning.'",
    radarData: [
      { axis: "Gaslighting", value: 10 },
      { axis: "Invalidation", value: 35 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 45 },
      { axis: "Blame Shift", value: 40 },
    ],
  },
  // ══ LEVEL 3 — Strategic / Coercive ══
  {
    keywords: ["you're being dramatic", "being dramatic"],
    manipulation_score: 5.8,
    toxicity_score: 5.5,
    primary_pattern: "soft_invalidation",
    secondary_pattern: "emotional_dismissal",
    confidence: "moderate",
    nuance: "Labels the other person's emotional response as performance rather than addressing what caused it. This is invalidation — it makes the reaction the problem instead of the behavior.",
    radarData: [
      { axis: "Gaslighting", value: 30 },
      { axis: "Invalidation", value: 72 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 35 },
      { axis: "Blame Shift", value: 40 },
    ],
  },
  {
    keywords: ["you're overreacting", "overreacting"],
    manipulation_score: 6.2,
    toxicity_score: 5.8,
    primary_pattern: "invalidation",
    secondary_pattern: "emotional_dismissal",
    confidence: "moderate",
    nuance: "This directly tells the other person their feelings are too big. It shifts the conversation from the issue to the reaction, making the emotional response the problem.",
    radarData: [
      { axis: "Gaslighting", value: 35 },
      { axis: "Invalidation", value: 78 },
      { axis: "Deflection", value: 52 },
      { axis: "Control", value: 40 },
      { axis: "Blame Shift", value: 45 },
    ],
  },
  {
    keywords: ["taking this personally", "not that serious", "think you're overreacting"],
    manipulation_score: 5.8,
    toxicity_score: 4.5,
    primary_pattern: "soft_invalidation",
    secondary_pattern: "emotional_dismissal",
    confidence: "moderate",
    nuance: "Reframes the other person's reaction as a personal flaw rather than engaging with the substance. May be insensitive rather than intentionally coercive, but the effect is still dismissive.",
    radarData: [
      { axis: "Gaslighting", value: 30 },
      { axis: "Invalidation", value: 70 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 35 },
      { axis: "Blame Shift", value: 40 },
    ],
  },
  {
    keywords: ["sensitive"],
    manipulation_score: 6.0,
    toxicity_score: 5.5,
    primary_pattern: "invalidation",
    secondary_pattern: "emotional_dismissal",
    confidence: "moderate",
    nuance: "Treats emotional sensitivity as a character flaw rather than a valid response. This is invalidation — it reframes the other person's feelings as the problem instead of the behavior that triggered them.",
    radarData: [
      { axis: "Gaslighting", value: 35 },
      { axis: "Invalidation", value: 80 },
      { axis: "Deflection", value: 45 },
      { axis: "Control", value: 40 },
      { axis: "Blame Shift", value: 55 },
    ],
  },
  {
    keywords: ["i guess i'm a terrible person", "i'm a terrible person then", "i can never do anything right", "i'm just a terrible person", "i guess i'm just a terrible person", "i'm always the bad guy", "i guess i ruin everything", "i ruin everything"],
    manipulation_score: 6.8,
    toxicity_score: 5.0,
    primary_pattern: "guilt_induction",
    secondary_pattern: "self_victimization",
    confidence: "moderate",
    nuance: "This uses self-criticism as a weapon — not genuine reflection, but a bid to make the other person reassure, comfort, or back down. The guilt pressure shifts focus from the issue to the speaker's feelings.",
    radarData: [
      { axis: "Gaslighting", value: 25 },
      { axis: "Invalidation", value: 40 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 65 },
      { axis: "Blame Shift", value: 70 },
    ],
  },
  {
    keywords: ["nothing i do is ever enough", "nothing is ever enough for you", "nothing i do is good enough"],
    manipulation_score: 6.2,
    toxicity_score: 5.0,
    primary_pattern: "guilt_induction",
    secondary_pattern: "self_victimization",
    confidence: "moderate",
    nuance: "Frames the speaker as perpetually inadequate to create guilt. This is softer than 'After all I've done' because it emphasizes helplessness rather than debt, but still pressures the other person to fix the feeling.",
    radarData: [
      { axis: "Gaslighting", value: 20 },
      { axis: "Invalidation", value: 35 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 60 },
      { axis: "Blame Shift", value: 65 },
    ],
  },
  {
    keywords: ["i talked to my friend because i needed advice", "i talked to my friend", "i needed advice", "my therapist told me to set boundaries"],
    manipulation_score: 2.6,
    toxicity_score: 1.8,
    primary_pattern: "neutral_accountability",
    secondary_pattern: "third_party_reference",
    confidence: "moderate",
    nuance: "Mentioning third parties for context or support is normal. This references outside input without weaponizing it against the other person.",
    radarData: [
      { axis: "Gaslighting", value: 4 },
      { axis: "Invalidation", value: 8 },
      { axis: "Deflection", value: 12 },
      { axis: "Control", value: 5 },
      { axis: "Blame Shift", value: 6 },
    ],
  },
  {
    keywords: ["ask anyone", "other people see it too", "im not the only one who thinks this", "i'm not the only one who thinks this"],
    manipulation_score: 5.8,
    toxicity_score: 4.2,
    primary_pattern: "triangulation",
    secondary_pattern: "social_leverage",
    confidence: "moderate",
    nuance: "Invokes vague outside opinion without naming anyone specific. This creates social pressure but lacks the direct consensus + blame combination that makes it stronger triangulation.",
    radarData: [
      { axis: "Gaslighting", value: 16 },
      { axis: "Invalidation", value: 36 },
      { axis: "Deflection", value: 52 },
      { axis: "Control", value: 38 },
      { axis: "Blame Shift", value: 30 },
    ],
  },
  {
    keywords: ["everyone else agrees", "everyone agrees with me", "everyone can see whats going on", "everyone can see what's going on"],
    manipulation_score: 7.2,
    toxicity_score: 5.5,
    primary_pattern: "triangulation",
    secondary_pattern: "false_consensus",
    confidence: "high",
    nuance: "Claims universal agreement to isolate the other person's position. This manufactures consensus — the implication is that disagreeing means you're the only one who doesn't see it.",
    radarData: [
      { axis: "Gaslighting", value: 20 },
      { axis: "Invalidation", value: 40 },
      { axis: "Deflection", value: 60 },
      { axis: "Control", value: 50 },
      { axis: "Blame Shift", value: 35 },
    ],
  },
  {
    keywords: ["my friends all think you're the problem", "my friends think you're the problem", "my family warned me about you", "everyone thinks you're the issue", "everyone thinks you're the problem", "they all think you're the problem", "people think you're the problem"],
    manipulation_score: 7.6,
    toxicity_score: 6.8,
    primary_pattern: "triangulation",
    secondary_pattern: "consensus_weaponization",
    confidence: "high",
    nuance: "Combines named third-party consensus with direct blame. This is the strongest form of triangulation — it weaponizes outside opinion to isolate you and frame you as the source of the problem.",
    radarData: [
      { axis: "Gaslighting", value: 28 },
      { axis: "Invalidation", value: 56 },
      { axis: "Deflection", value: 68 },
      { axis: "Control", value: 58 },
      { axis: "Blame Shift", value: 74 },
    ],
  },
  {
    keywords: ["no one else has a problem", "most people see it differently", "everyone else thinks"],
    manipulation_score: 5.5,
    toxicity_score: 3.5,
    primary_pattern: "triangulation",
    secondary_pattern: "social_comparison",
    confidence: "moderate",
    nuance: "References social norms to make the other person feel like an outlier. Could be an honest observation, but often serves to isolate your perspective by implying you're the only one with the problem.",
    radarData: [
      { axis: "Gaslighting", value: 25 },
      { axis: "Invalidation", value: 45 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 40 },
      { axis: "Blame Shift", value: 30 },
    ],
  },
  {
    keywords: ["other people manage", "others manage", "coworkers agree", "my coworkers agree"],
    manipulation_score: 5.8,
    toxicity_score: 3.8,
    primary_pattern: "soft_invalidation",
    secondary_pattern: "social_comparison",
    confidence: "moderate",
    nuance: "Compares you unfavorably to others, implying you're uniquely deficient. May stem from frustration rather than calculated manipulation, but the effect is still dismissive.",
    radarData: [
      { axis: "Gaslighting", value: 15 },
      { axis: "Invalidation", value: 50 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 40 },
      { axis: "Blame Shift", value: 30 },
    ],
  },
  {
    keywords: ["everybody says", "everyone says"],
    manipulation_score: 7.0,
    toxicity_score: 5.2,
    primary_pattern: "triangulation",
    secondary_pattern: "consensus_manufacturing",
    confidence: "high",
    nuance: "Claims broad agreement without naming anyone. The vagueness makes it likely exaggerated — this manufactures consensus to pressure compliance rather than reporting genuine feedback.",
    radarData: [
      { axis: "Gaslighting", value: 25 },
      { axis: "Invalidation", value: 55 },
      { axis: "Deflection", value: 60 },
      { axis: "Control", value: 50 },
      { axis: "Blame Shift", value: 40 },
    ],
  },
  {
    keywords: ["taking it personally"],
    manipulation_score: 5.8,
    toxicity_score: 4.0,
    primary_pattern: "soft_invalidation",
    secondary_pattern: "tone_policing",
    confidence: "moderate",
    nuance: "Turns the conversation into a critique of how you react rather than engaging with what you're reacting to. The issue shifts from the behavior to your sensitivity.",
    radarData: [
      { axis: "Gaslighting", value: 20 },
      { axis: "Invalidation", value: 55 },
      { axis: "Deflection", value: 45 },
      { axis: "Control", value: 30 },
      { axis: "Blame Shift", value: 25 },
    ],
  },
  {
    keywords: ["joke"],
    manipulation_score: 6.5,
    toxicity_score: 5.0,
    primary_pattern: "minimization",
    secondary_pattern: "deflection",
    confidence: "moderate",
    nuance: "Reframes hurtful behavior as humor to avoid accountability. If it was 'just a joke,' then your reaction becomes the problem — a form of minimization that dismisses legitimate impact.",
    radarData: [
      { axis: "Gaslighting", value: 40 },
      { axis: "Invalidation", value: 82 },
      { axis: "Deflection", value: 88 },
      { axis: "Control", value: 50 },
      { axis: "Blame Shift", value: 55 },
    ],
  },
  // ══ LEVEL 4 — High / Forensic ══
  {
    keywords: ["that never happened"],
    manipulation_score: 7.5,
    toxicity_score: 7.0,
    primary_pattern: "gaslighting",
    secondary_pattern: "reality_denial",
    confidence: "high",
    nuance: "This is a flat denial of shared reality — not a difference of perspective, but a claim that the event didn't occur. It's stronger than 'That's not what happened' because it erases the event entirely rather than disputing details.",
    radarData: [
      { axis: "Gaslighting", value: 82 },
      { axis: "Invalidation", value: 55 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 65 },
      { axis: "Blame Shift", value: 50 },
    ],
  },
  {
    keywords: ["you made that up"],
    manipulation_score: 8.5,
    toxicity_score: 8.2,
    primary_pattern: "gaslighting",
    secondary_pattern: "reality_denial",
    confidence: "high",
    nuance: "Goes beyond denying an event — it accuses the other person of inventing it. This attacks credibility directly and is the most aggressive form of reality denial.",
    radarData: [
      { axis: "Gaslighting", value: 95 },
      { axis: "Invalidation", value: 75 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 82 },
      { axis: "Blame Shift", value: 70 },
    ],
  },
  {
    keywords: ["never said that", "imagining things"],
    manipulation_score: 8.0,
    toxicity_score: 7.5,
    primary_pattern: "gaslighting",
    secondary_pattern: "reality_denial",
    confidence: "high",
    nuance: "Directly denies the other person's lived experience. This is textbook gaslighting — it doesn't just disagree, it tells you that what you experienced didn't happen.",
    radarData: [
      { axis: "Gaslighting", value: 90 },
      { axis: "Invalidation", value: 65 },
      { axis: "Deflection", value: 55 },
      { axis: "Control", value: 72 },
      { axis: "Blame Shift", value: 60 },
    ],
  },
  {
    keywords: ["after all i've done", "after everything i've done"],
    manipulation_score: 7.8,
    toxicity_score: 7.0,
    primary_pattern: "guilt_induction",
    secondary_pattern: "emotional_blackmail",
    confidence: "high",
    nuance: "This is stronger than generic self-pity because it pressures through indebtedness. Past effort is framed as emotional debt — the implication is that you owe a specific response because of what the speaker claims they've done.",
    radarData: [
      { axis: "Gaslighting", value: 40 },
      { axis: "Invalidation", value: 55 },
      { axis: "Deflection", value: 45 },
      { axis: "Control", value: 88 },
      { axis: "Blame Shift", value: 82 },
    ],
  },
  {
    keywords: ["if you loved me", "if you really loved me"],
    manipulation_score: 9.1,
    toxicity_score: 8.5,
    primary_pattern: "guilt_induction",
    secondary_pattern: "emotional_blackmail",
    confidence: "high",
    nuance: "Turns love into a transaction — the implication is that compliance equals love, and refusal equals proof you don't care. This is one of the highest-harm forms of guilt-tripping.",
    radarData: [
      { axis: "Gaslighting", value: 60 },
      { axis: "Invalidation", value: 75 },
      { axis: "Deflection", value: 50 },
      { axis: "Control", value: 90 },
      { axis: "Blame Shift", value: 85 },
    ],
  },
];

export function routeAnalysis(message: string): AnalysisResult {
  const normalizeForMatch = (input: string) =>
    input
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[’‘`´]/g, "'")
      .replace(/[^a-z0-9'\s]/g, " ")
      .replace(/'/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const normalizedMessage = normalizeForMatch(message);
  const hasPhrase = (phrases: string[]) =>
    phrases.some((phrase) => normalizedMessage.includes(normalizeForMatch(phrase)));

  const HEALTHY_PAUSE_PHRASES = [
    "i need space right now",
    "i need some space",
    "lets talk later",
    "lets discuss later",
    "i dont want to continue this right now",
    "im not ready to discuss this right now",
    "can we revisit this later",
    "can we talk later",
  ];

  const COERCIVE_SHUTDOWN_PHRASES = [
    "fine i just wont talk anymore",
    "i just wont talk anymore",
    "ill just keep quiet then",
    "ill just keep quiet",
    "forget it",
    "you win",
  ];

  const BOUNDARY_ESCALATORS = [
    "because of you",
    "this is your fault",
    "you always",
    "you never",
    "as usual",
    "if you cared",
    "if you loved me",
    "after all ive done",
    "after everything ive done",
    "you made me",
    "you are impossible",
    "youre impossible",
    "dont bother",
    "whatever",
  ];

  const isHealthyPause = hasPhrase(HEALTHY_PAUSE_PHRASES);
  const hasCoerciveShutdown = hasPhrase(COERCIVE_SHUTDOWN_PHRASES);
  const hasBoundaryEscalator = hasPhrase(BOUNDARY_ESCALATORS);

  // Semantic hard-protection: pause/disengagement alone is not manipulation
  if (isHealthyPause && !hasCoerciveShutdown && !hasBoundaryEscalator) {
    const manipulation_score = 1.5;
    const toxicity_score = 1.0;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 2 },
        { axis: "Invalidation", value: 3 },
        { axis: "Deflection", value: 8 },
        { axis: "Control", value: 2 },
        { axis: "Blame Shift", value: 2 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "high",
      nuance:
        "This is healthy boundary-setting. The speaker postpones discussion without blame, punishment, or guilt pressure.",
      primary_pattern: "neutral_accountability",
      secondary_pattern: "healthy_boundary",
    };
  }

  // Coercive shutdown should remain clearly above healthy pause language
  if (hasCoerciveShutdown) {
    const manipulation_score = 5.2;
    const toxicity_score = 4.2;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 12 },
        { axis: "Invalidation", value: 38 },
        { axis: "Deflection", value: 52 },
        { axis: "Control", value: 55 },
        { axis: "Blame Shift", value: 44 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "moderate",
      nuance:
        "The speaker frames silence as pressure or punishment rather than a neutral pause. This is withdrawal used as leverage, not genuine disengagement.",
      primary_pattern: "passive_aggression",
      secondary_pattern: "coercive_withdrawal",
    };
  }

  // Check anchors in order (first match wins), with normalized semantic matching
  for (const anchor of ANCHORS) {
    if (anchor.keywords.some((kw) => normalizedMessage.includes(normalizeForMatch(kw)))) {
      const score = anchor.manipulation_score;
      return {
        score,
        verdict: verdictFromScore(score),
        radarData: anchor.radarData,
        flagCount: FLAG_COUNT_BY_SCORE(score),
        manipulation_score: anchor.manipulation_score,
        toxicity_score: anchor.toxicity_score,
        confidence: anchor.confidence,
        nuance: anchor.nuance,
        primary_pattern: anchor.primary_pattern,
        secondary_pattern: anchor.secondary_pattern,
      };
    }
  }

  // ── Dynamic triangulation: third-party consensus + direct blame ──
  const THIRD_PARTY_REFS = [
    "my friends", "my family", "my coworkers", "my colleagues", "my parents",
    "my mom", "my dad", "my sister", "my brother", "my therapist",
    "everyone", "everybody", "people", "they all", "we all",
    "no one", "nobody",
  ];
  const CONSENSUS_LANG = [
    "all think", "all say", "all agree", "all know", "all see",
    "agrees", "think you", "say you", "told me you", "warned me",
    "see it too", "noticed", "can tell",
  ];
  const BLAME_TARGETS = [
    "youre the problem", "you are the problem", "youre toxic",
    "youre the issue", "you are the issue", "about you",
    "youre wrong", "you are wrong", "youre difficult",
    "youre the one", "you are the one", "youre crazy",
    "youre controlling", "youre manipulative", "youre abusive",
    "youre selfish", "youre narcissistic",
  ];

  const hasThirdParty = THIRD_PARTY_REFS.some((r) => normalizedMessage.includes(r));
  const hasConsensus = CONSENSUS_LANG.some((c) => normalizedMessage.includes(c));
  const hasBlame = BLAME_TARGETS.some((b) => normalizedMessage.includes(b));

  // Level D: consensus + blame (strongest)
  if (hasThirdParty && hasConsensus && hasBlame) {
    const manipulation_score = 7.6;
    const toxicity_score = 6.8;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 28 },
        { axis: "Invalidation", value: 56 },
        { axis: "Deflection", value: 68 },
        { axis: "Control", value: 58 },
        { axis: "Blame Shift", value: 74 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "high",
      nuance: "This combines third-party consensus with direct blame — the strongest form of triangulation. Outside opinion is weaponized to isolate you and frame you as the sole source of the problem.",
      primary_pattern: "triangulation",
      secondary_pattern: "consensus_weaponization",
    };
  }

  // Level C: third-party + consensus (no direct blame)
  if (hasThirdParty && hasConsensus) {
    const manipulation_score = 7.2;
    const toxicity_score = 5.5;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 20 },
        { axis: "Invalidation", value: 40 },
        { axis: "Deflection", value: 60 },
        { axis: "Control", value: 50 },
        { axis: "Blame Shift", value: 35 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "high",
      nuance: "Invokes third-party agreement as social leverage. The implication is that everyone else sees it — making disagreement feel like denial rather than a valid position.",
      primary_pattern: "triangulation",
      secondary_pattern: "false_consensus",
    };
  }

  // Level B+: third-party + blame (no consensus language, but still targeted)
  if (hasThirdParty && hasBlame) {
    const manipulation_score = 6.8;
    const toxicity_score = 5.2;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 22 },
        { axis: "Invalidation", value: 48 },
        { axis: "Deflection", value: 58 },
        { axis: "Control", value: 48 },
        { axis: "Blame Shift", value: 62 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "moderate",
      nuance: "Combines third-party reference with direct blame. The outside perspective is used to add weight to the accusation rather than to share genuine feedback.",
      primary_pattern: "triangulation",
      secondary_pattern: "externalized_blame",
    };
  }

  // ── Short passive-aggressive / sarcastic phrases ──
  const SHORT_PA_PHRASES = [
    "interesting", "wow", "good to know", "must be nice", "sure",
    "okay then", "right", "cool", "noted", "whatever you say",
  ];
  const wordCount = normalizedMessage.split(" ").length;
  if (wordCount <= 5 && SHORT_PA_PHRASES.some((p) => normalizedMessage === p || normalizedMessage === p + " ")) {
    const manipulation_score = 5.2;
    const toxicity_score = 4.0;
    return {
      score: manipulation_score,
      verdict: verdictFromScore(manipulation_score),
      radarData: [
        { axis: "Gaslighting", value: 10 },
        { axis: "Invalidation", value: 45 },
        { axis: "Deflection", value: 60 },
        { axis: "Control", value: 35 },
        { axis: "Blame Shift", value: 20 },
      ],
      flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
      manipulation_score,
      toxicity_score,
      confidence: "moderate" as const,
      nuance:
        "Short, tonally loaded response detected. These phrases often function as passive-aggressive dismissal — conveying disapproval without explicit confrontation.",
      primary_pattern: "passive_aggression",
      secondary_pattern: "dismissive_ambiguity",
    };
  }

  // Deterministic fallback (no randomness): stable moderate score for unanchored phrases
  const hash = normalizedMessage.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const manipulation_score = parseFloat((4.8 + (hash % 15) / 10).toFixed(1)); // 4.8–6.2
  const toxicity_score = parseFloat((3.8 + (hash % 19) / 10).toFixed(1)); // 3.8–5.6

  return {
    score: manipulation_score,
    verdict: verdictFromScore(manipulation_score),
    radarData: [
      { axis: "Gaslighting", value: 36 + (hash % 18) },
      { axis: "Invalidation", value: 40 + (hash % 16) },
      { axis: "Deflection", value: 44 + (hash % 14) },
      { axis: "Control", value: 34 + (hash % 17) },
      { axis: "Blame Shift", value: 30 + (hash % 15) },
    ],
    flagCount: FLAG_COUNT_BY_SCORE(manipulation_score),
    manipulation_score,
    toxicity_score,
    confidence: confidenceFromScore(manipulation_score),
    nuance:
      "This phrase contains mixed signals. It may reflect genuine disagreement or stress, but certain elements suggest possible manipulation depending on context and pattern of use.",
    primary_pattern: "ambiguous_responsibility",
    secondary_pattern: "deflection",
  };
}
